import { ItemID } from "types/common";
import { TaxonomyItem } from "types/entities/Taxonomy";

import React from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { defaultTimeToLive } from "consts";

import { QueryKeys } from "api/queryKeys";
import { getItemsByIds } from "api/useGetItemsByIds";

import { cacheTaxonomyItems, chunkIds, getCachedTaxonomyItem, splitCachedIds } from "./taxonomyItemsCache";

// Keeps each GET /item/?ids= URL short
const BATCH_SIZE = 100;

// Bulk taxonomy lookup that requests only the ids not already in the cache
export const useGetTaxonomyItemsInBatches = (itemIds: ItemID[], enabled = true) => {
  const queryClient = useQueryClient();

  // Taken once per id list; cached items are kept here so they survive the cache dropping them later
  const { cachedItems, missingIds } = React.useMemo(
    () => splitCachedIds(itemIds, (id) => getCachedTaxonomyItem(queryClient, id)),
    [itemIds, queryClient]
  );
  const batches = React.useMemo(() => chunkIds([...missingIds].sort(), BATCH_SIZE), [missingIds]);

  const results = useQueries({
    queries: batches.map((batch) => ({
      queryKey: [QueryKeys.GET_LIST_MATCH_ITEMS, batch],
      queryFn: async () => {
        const items = await getItemsByIds(batch)();
        cacheTaxonomyItems(queryClient, items);
        return items;
      },
      enabled,
      ...defaultTimeToLive,
    })),
  });

  // useQueries returns a new array every render; key the merged list on the batches' data instead
  const dataKey = results.map((result) => result.dataUpdatedAt).join(",");
  const items = React.useMemo<TaxonomyItem[]>(
    () => [...cachedItems, ...results.flatMap((result) => result.data || [])],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cachedItems, dataKey]
  );

  return {
    items,
    isLoading: enabled && results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  };
};
